import React, { useState } from 'react';
import AppLayout from '@/components/app/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Filter, Search, Syringe, Heart, Droplets, Stethoscope, Zap, Microscope, Sparkles } from 'lucide-react';
const AppTreatments = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const treatments = [{
    name: "Botox",
    description: "Smooth fine lines and wrinkles with precision injections",
    credits: 8,
    category: "Injectables",
    tier: "Essential",
    icon: Syringe,
    href: "/app/treatments/botox"
  }, {
    name: "Lip Filler",
    description: "Add volume and definition to your lips",
    credits: 10,
    category: "Injectables",
    tier: "Signature",
    icon: Heart,
    href: "/app/treatments/lip-filler"
  }, {
    name: "AquaFirme Facial",
    description: "Deep hydration with vitamin infusion",
    credits: 4,
    category: "Skin",
    tier: "Essential",
    icon: Droplets,
    href: "/app/treatments/aquafirme"
  }, {
    name: "Laser Whitening",
    description: "Professional teeth whitening treatment",
    credits: 6,
    category: "Smile",
    tier: "Signature",
    icon: Sparkles,
    href: "/app/treatments/laser-whitening"
  }, {
    name: "TMJ Botox",
    description: "Alleviate jaw tension and improve smile balance",
    credits: 12,
    category: "Wellness",
    tier: "Transformational",
    icon: Stethoscope,
    href: "/app/treatments/tmj-botox"
  }, {
    name: "RF Microneedling",
    description: "Collagen induction with radiofrequency",
    credits: 8,
    category: "Skin",
    tier: "Signature",
    icon: Microscope,
    href: "/app/treatments/rf-microneedling"
  }];
  const filters = ["All", "Smile", "Skin", "Injectables", "Wellness"];
  const tierFilters = ["All Tiers", "Essential", "Signature", "Transformational"];
  const filteredTreatments = activeFilter === "All" ? treatments : treatments.filter(treatment => treatment.category === activeFilter);
  return <AppLayout>
      <div className="px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-juv-deep-chestnut">Treatments</h1>
          <div className="flex gap-2">
            <Button variant="outline" size="icon">
              <Search className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="icon">
              <Filter className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {filters.map(filter => <Button key={filter} onClick={() => setActiveFilter(filter)} variant={activeFilter === filter ? "default" : "outline"} size="sm" className={`whitespace-nowrap ${activeFilter === filter ? 'bg-juv-terracotta hover:bg-juv-deep-chestnut' : ''}`}>
              {filter}
            </Button>)}
        </div>

        {/* Tier Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tierFilters.map(tier => <Button key={tier} variant="outline" size="sm" className="whitespace-nowrap text-xs">
              {tier}
            </Button>)}
        </div>

        {/* Credit Range Indicator */}
        <div className="mb-6 p-3 bg-juv-soft-blush rounded-lg">
          <p className="text-sm text-juv-deep-chestnut">
            <span className="font-medium">Credit Range:</span> 1-24+ credits
          </p>
          <p className="text-xs text-gray-600 mt-1">
            Popular tags: Glow • Confidence • Anti-Aging • Whitening
          </p>
        </div>

        {/* Treatments Grid */}
        <div className="space-y-4">
          {filteredTreatments.map((treatment, index) => <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardContent className="p-0">
                <div className="flex bg-slate-50">
                  {/* Treatment Icon */}
                  <div className="w-20 h-20 bg-juv-soft-blush flex items-center justify-center">
                    <treatment.icon className="w-8 h-8 text-juv-terracotta" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 p-4">
                    {/* Category & Tier Tags */}
                    <div className="flex gap-2 mb-2">
                      <span className="text-xs bg-juv-rose-clay text-juv-deep-chestnut px-2 py-1 rounded">
                        {treatment.category}
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {treatment.tier}
                      </span>
                    </div>
                    
                    {/* Title & Description */}
                    <h3 className="font-semibold text-juv-deep-chestnut mb-1">
                      {treatment.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {treatment.description}
                    </p>
                    
                    {/* Pricing & Actions */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-juv-terracotta font-semibold">
                          {treatment.credits} Credits
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button asChild size="sm" variant="outline">
                          <Link to={treatment.href}>View Details</Link>
                        </Button>
                        <Button asChild size="sm" className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
                          <Link to="/app/book">Book with Credits</Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>

        {/* Take Quiz CTA */}
        <Card className="mt-8 bg-gradient-to-r from-juv-soft-blush to-juv-rose-clay">
          <CardContent className="p-6 text-center">
            <Sparkles className="w-8 h-8 text-juv-terracotta mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-juv-deep-chestnut mb-2">
              Not Sure What to Book?
            </h3>
            <p className="text-gray-600 mb-4">
              Take our personalized quiz to discover the perfect treatment for your goals.
            </p>
            <Button asChild className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
              <Link to="/quiz">Take the Glow Quiz</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>;
};
export default AppTreatments;